/*
  A11y Observers – one-file helpers for waiting, observing, and labeling
  across light DOM and (open) Shadow DOMs. Supports multiple instances,
  timeouts, and cancellation via AbortSignal.

  Exposes a global `A11yWait` (browser) and CommonJS export (Node bundlers).

  API:
    - waitForElement(selector, { root, timeout, signal }) -> Promise<Element>
    - waitForOpenShadowRootOf(host, { timeout, signal }) -> Promise<ShadowRoot>
    - observeMatches(selector, onAdd, { root }) -> { disconnect }
    - labelWhenPresent(selector, ariaLabel, opts) -> Promise<Element>
    - labelWhenPresentDeep(selector, ariaLabel, opts) -> Promise<Element>
      (opts: { root=document, timeout=0, ifMissingOnly=false, signal, watchClosed=false })
    - observeMatchesDeep(selector, onAdd, opts) -> { disconnect }
    - observeHostsAndLabelInside(hostSelector, targetSelector, ariaLabel, optsForLabel)

  Notes:
    • “Deep” functions traverse all open (and optionally captured closed) shadow roots.
    • Closed shadow roots require hooking `attachShadow` BEFORE components create them.
*/

(function () {
  'use strict';

  /**
   * Wait for the first element matching `selector` to appear under `root`.
   * Resolves with that element. Supports timeout and AbortSignal.
   * @param {string} selector
   * @param {{root?: Document|Element, timeout?: number, signal?: AbortSignal}} [opts]
   * @returns {Promise<Element>}
   */
  function waitForElement(selector, { root = document, timeout = 0, signal } = {}) {
    if (!selector) throw new Error('waitForElement(selector) requires a selector.');

    const immediate = root.querySelector?.(selector);
    if (immediate) return Promise.resolve(immediate);

    return new Promise((resolve, reject) => {
      const target = root.nodeType === Node.DOCUMENT_NODE
        ? /** @type {Document} */(root).documentElement
        : /** @type {Element} */(root);

      let timerId = null;

      const cleanup = () => {
        obs.disconnect();
        if (timerId) clearTimeout(timerId);
        signal?.removeEventListener?.('abort', onAbort);
      };

      const onAbort = () => {
        cleanup();
        reject(new DOMException('Operation aborted', 'AbortError'));
      };

      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue; // Element
            /** @type {Element} */ const el = node;
            if (el.matches?.(selector)) { cleanup(); return resolve(el); }
            const found = el.querySelector?.(selector);
            if (found) { cleanup(); return resolve(found); }
          }
        }
      });

      obs.observe(target, { childList: true, subtree: true });

      if (timeout > 0) {
        timerId = setTimeout(() => {
          cleanup();
          reject(new Error(`Timed out waiting for "${selector}" after ${timeout}ms.`));
        }, timeout);
      }

      if (signal) {
        if (signal.aborted) return onAbort();
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }

  /**
   * Wait for an **open** shadow root to exist on a host element.
   * Uses a lightweight rAF loop (with optional timeout).
   * @param {Element} host
   * @param {{timeout?: number, signal?: AbortSignal}} [opts]
   * @returns {Promise<ShadowRoot>}
   */
  function waitForOpenShadowRootOf(host, { timeout = 0, signal } = {}) {
    if (!(host instanceof Element)) throw new Error('waitForOpenShadowRootOf(host) requires an Element host.');
    if (host.shadowRoot) return Promise.resolve(host.shadowRoot);

    return new Promise((resolve, reject) => {
      let rafId = 0, timerId = 0, done = false;
      const start = performance.now();

      const cleanup = () => {
        done = true;
        cancelAnimationFrame(rafId);
        if (timerId) clearTimeout(timerId);
        signal?.removeEventListener?.('abort', onAbort);
      };

      const tick = () => {
        if (done) return;
        if (host.shadowRoot) { cleanup(); return resolve(host.shadowRoot); }
        if (timeout && performance.now() - start >= timeout) {
          cleanup(); return reject(new Error('Timed out waiting for open shadowRoot.'));
        }
        rafId = requestAnimationFrame(tick);
      };

      const onAbort = () => {
        cleanup();
        reject(new DOMException('Operation aborted', 'AbortError'));
      };

      if (signal) {
        if (signal.aborted) return onAbort();
        signal.addEventListener('abort', onAbort, { once: true });
      }

      if (timeout > 0) {
        timerId = setTimeout(() => {
          cleanup();
          reject(new Error('Timed out waiting for open shadowRoot.'));
        }, timeout + 50);
      }

      tick();
    });
  }

  /**
   * Observe current + future elements that match `selector` under `root`,
   * and call `onAdd(el)` once per element. Returns { disconnect }.
   * @param {string} selector
   * @param {(el:Element)=>void} onAdd
   * @param {{root?: Document|Element}} [opts]
   */
  function observeMatches(selector, onAdd, { root = document } = {}) {
    if (!selector || typeof onAdd !== 'function') {
      throw new Error('observeMatches(selector, onAdd, {root}) requires a selector and a function.');
    }

    const processed = new WeakSet();

    const markAndHandle = (el) => {
      if (!(el instanceof Element) || processed.has(el)) return;
      processed.add(el);
      onAdd(el);
    };

    // Handle existing matches immediately
    root.querySelectorAll?.(selector).forEach(markAndHandle);

    // Observe future additions
    const target = root.nodeType === Node.DOCUMENT_NODE ? /** @type {Document} */(root).documentElement : root;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue; // Element
          const el = /** @type {Element} */ (node);
          if (el.matches?.(selector)) markAndHandle(el);
          el.querySelectorAll?.(selector).forEach(markAndHandle);
        }
      }
    });
    mo.observe(target, { childList: true, subtree: true });

    return { disconnect: () => mo.disconnect() };
  }

  /**
   * Wait for the first element matching `selector` to exist under `root`,
   * then set its aria-label. Resolves with that element.
   * @param {string} selector
   * @param {string} ariaLabel
   * @param {{root?: Document|ShadowRoot|Element, timeout?: number, ifMissingOnly?: boolean, signal?: AbortSignal}} [opts]
   * @returns {Promise<Element>}
   */
  function labelWhenPresent(selector, ariaLabel, opts = {}) {
    const {
      root = document,
      timeout = 0,
      ifMissingOnly = false,
      signal,
    } = opts;

    if (!selector || !ariaLabel) {
      throw new Error('labelWhenPresent(selector, ariaLabel, [opts]) requires both selector and ariaLabel.');
    }

    const apply = (el) => {
      if (!ifMissingOnly || !el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', ariaLabel);
      }
      return el;
    };

    return new Promise((resolve, reject) => {
      // Immediate check
      const immediate = root.querySelector?.(selector);
      if (immediate) return resolve(apply(immediate));

      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue; // Element
            const el = /** @type {Element} */ (node);
            if (el.matches?.(selector)) { cleanup(); return resolve(apply(el)); }
            const found = el.querySelector?.(selector);
            if (found) { cleanup(); return resolve(apply(found)); }
          }
        }
      });

      const target = root && root.nodeType === Node.DOCUMENT_NODE
        ? /** @type {Document} */ (root).documentElement
        : /** @type {Element|ShadowRoot} */ (root);

      observer.observe(target, { childList: true, subtree: true });

      let timerId = null;
      if (timeout > 0) {
        timerId = setTimeout(() => {
          cleanup();
          reject(new Error(`Timed out waiting for "${selector}" after ${timeout}ms.`));
        }, timeout);
      }

      function onAbort() {
        cleanup();
        reject(new DOMException('Operation aborted', 'AbortError'));
      }

      if (signal) {
        if (signal.aborted) return onAbort();
        signal.addEventListener('abort', onAbort, { once: true });
      }

      function cleanup() {
        observer.disconnect();
        if (timerId) clearTimeout(timerId);
        if (signal) signal.removeEventListener?.('abort', onAbort);
      }
    });
  }

  /**
   * Like labelWhenPresent, but traverses across open (and optionally captured closed) shadow roots.
   * Finds the first element matching `selector` anywhere under `root`, then sets aria-label.
   * @param {string} selector
   * @param {string} ariaLabel
   * @param {{root?: Document|ShadowRoot|Element, timeout?: number, ifMissingOnly?: boolean, signal?: AbortSignal, watchClosed?: boolean}} [opts]
   * @returns {Promise<Element>}
   */
  function labelWhenPresentDeep(selector, ariaLabel, opts = {}) {
    const {
      root = document,
      timeout = 0,
      ifMissingOnly = false,
      signal,
      watchClosed = false,
    } = opts;

    if (!selector || !ariaLabel) {
      throw new Error('labelWhenPresentDeep(selector, ariaLabel, [opts]) requires both selector and ariaLabel.');
    }

    const observers = new Set(); // MutationObservers
    const seenRoots = new WeakSet(); // ShadowRoot/Document visited
    const closedMap = watchClosed ? new WeakMap() : null; // host -> closed ShadowRoot
    let restoreAttach = null;
    let timerId = null;

    const apply = (el) => {
      if (!ifMissingOnly || !el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', ariaLabel);
      }
      return el;
    };

    function cleanup() {
      for (const obs of observers) obs.disconnect();
      observers.clear();
      if (timerId) clearTimeout(timerId);
      if (restoreAttach) restoreAttach();
      if (signal) signal.removeEventListener?.('abort', onAbort);
    }

    function onAbort() {
      cleanup();
      rejectPromise(new DOMException('Operation aborted', 'AbortError'));
    }

    // Traverse a DOM root (Document or ShadowRoot) and nested open (and known closed) shadow roots.
    function forEachRoot(startRoot, fn) {
      const stack = [startRoot];
      const visited = new Set();
      while (stack.length) {
        const r = stack.pop();
        if (!r || visited.has(r)) continue;
        visited.add(r);
        fn(r);
        // Discover nested shadow roots
        const walker = document.createTreeWalker(r, NodeFilter.SHOW_ELEMENT);
        let n;
        while ((n = walker.nextNode())) {
          // Open roots
          if (/** @type {Element} */(n).shadowRoot) stack.push(/** @type {Element} */(n).shadowRoot);
          // Known closed roots
          if (closedMap && closedMap.has(n)) stack.push(closedMap.get(n));
        }
      }
    }

    // Query across all known roots
    function queryDeep(sel, startRoot) {
      let found = null;
      forEachRoot(startRoot, (r) => {
        if (!found && r.querySelector) {
          const hit = r.querySelector(sel);
          if (hit) found = hit;
        }
      });
      return found;
    }

    function observeRoot(r) {
      if (!r || seenRoots.has(r)) return;
      seenRoots.add(r);
      const target = r.nodeType === Node.DOCUMENT_NODE ? /** @type {Document} */(r).documentElement : r;
      const obs = new MutationObserver(() => {
        const candidate = queryDeep(selector, baseRoot);
        if (candidate) { cleanup(); resolvePromise(apply(candidate)); }
      });
      obs.observe(target, { childList: true, subtree: true });
      observers.add(obs);
    }

    function hookAttachShadow() {
      const original = Element.prototype.attachShadow;
      if (typeof original !== 'function' || original.__a11yPatched) return;
      Element.prototype.attachShadow = function(init) {
        const sr = original.call(this, init);
        // Store closed roots if requested
        if (closedMap && init && init.mode === 'closed') {
          closedMap.set(this, sr);
        }
        // Observe every new shadow root immediately
        observeRoot(sr);
        return sr;
      };
      Object.defineProperty(Element.prototype.attachShadow, '__a11yPatched', { value: true });
      restoreAttach = () => { Element.prototype.attachShadow = original; };
    }

    let resolvePromise, rejectPromise;
    const p = new Promise((res, rej) => { resolvePromise = res; rejectPromise = rej; });

    if (signal) {
      if (signal.aborted) return Promise.reject(new DOMException('Operation aborted', 'AbortError'));
      signal.addEventListener('abort', onAbort, { once: true });
    }
    if (timeout > 0) {
      timerId = setTimeout(() => {
        cleanup();
        rejectPromise(new Error(`Timed out waiting for "${selector}" after ${timeout}ms.`));
      }, timeout);
    }

    const baseRoot = (root && root.nodeType === Node.DOCUMENT_NODE) ? root : (root.getRootNode?.() || document);

    // 1) Immediate check
    const immediate = queryDeep(selector, baseRoot);
    if (immediate) { cleanup(); return Promise.resolve(apply(immediate)); }

    // 2) Observe document + existing shadow roots; also hook attachShadow for future ones.
    hookAttachShadow();
    forEachRoot(baseRoot, observeRoot);

    return p;
  }

  /**
   * Observe current + future matches across ALL open (and optionally captured closed) shadow roots.
   * Calls onAdd once per element. Returns { disconnect }.
   * @param {string} selector
   * @param {(el:Element)=>void} onAdd
   * @param {{root?: Document|ShadowRoot|Element, watchClosed?: boolean}} [opts]
   */
  function observeMatchesDeep(selector, onAdd, { root = document, watchClosed = false } = {}) {
    if (!selector || typeof onAdd !== 'function') {
      throw new Error('observeMatchesDeep(selector, onAdd, opts) requires a selector and a function.');
    }

    const processed = new WeakSet();
    const observers = new Set();
    const seenRoots = new WeakSet();
    const closedMap = watchClosed ? new WeakMap() : null; // host -> closed ShadowRoot
    let restoreAttach = null;

    const markAndHandle = (el) => {
      if (!(el instanceof Element) || processed.has(el)) return;
      processed.add(el);
      onAdd(el);
    };

    function forEachRoot(startRoot, fn) {
      const stack = [startRoot];
      const visited = new Set();
      while (stack.length) {
        const r = stack.pop();
        if (!r || visited.has(r)) continue;
        visited.add(r);
        fn(r);
        const walker = document.createTreeWalker(r, NodeFilter.SHOW_ELEMENT);
        let n;
        while ((n = walker.nextNode())) {
          if (/** @type {Element} */(n).shadowRoot) stack.push(/** @type {Element} */(n).shadowRoot);
          if (closedMap && closedMap.has(n)) stack.push(closedMap.get(n));
        }
      }
    }

    function observeRoot(r) {
      if (!r || seenRoots.has(r)) return;
      seenRoots.add(r);
      // Handle existing in this root now
      r.querySelectorAll?.(selector).forEach(markAndHandle);
      const target = r.nodeType === Node.DOCUMENT_NODE ? /** @type {Document} */(r).documentElement : r;
      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            const el = /** @type {Element} */ (node);
            if (el.matches?.(selector)) markAndHandle(el);
            el.querySelectorAll?.(selector).forEach(markAndHandle);
          }
        }
      });
      obs.observe(target, { childList: true, subtree: true });
      observers.add(obs);
    }

    function hookAttachShadow() {
      const original = Element.prototype.attachShadow;
      if (typeof original !== 'function' || original.__a11yPatchedDeep) return;
      Element.prototype.attachShadow = function(init) {
        const sr = original.call(this, init);
        if (closedMap && init && init.mode === 'closed') {
          closedMap.set(this, sr);
        }
        observeRoot(sr);
        return sr;
      };
      Object.defineProperty(Element.prototype.attachShadow, '__a11yPatchedDeep', { value: true });
      restoreAttach = () => { Element.prototype.attachShadow = original; };
    }

    const baseRoot = (root && root.nodeType === Node.DOCUMENT_NODE) ? root : (root.getRootNode?.() || document);

    hookAttachShadow();
    forEachRoot(baseRoot, observeRoot);

    return { disconnect: () => { for (const o of observers) o.disconnect(); observers.clear(); if (restoreAttach) restoreAttach(); } };
  }

  /**
   * Convenience: handle multiple shadow-host instances. For each host that appears,
   * wait for its (open) shadowRoot, then observe targets inside it and set aria-label
   * on current + future matches.
   * @param {string} hostSelector
   * @param {string} targetSelector
   * @param {string} ariaLabel
   * @param {{ ifMissingOnly?: boolean }} [opts]
   * @returns {{ disconnect: ()=>void }}
   */
  function observeHostsAndLabelInside(hostSelector, targetSelector, ariaLabel, { ifMissingOnly = true } = {}) {
    const subs = new Set();

    const outer = observeMatches(hostSelector, async (host) => {
      try {
        const sr = await waitForOpenShadowRootOf(host, { timeout: 15000 });
        const sub = observeMatches(targetSelector, (el) => {
          if (!ifMissingOnly || !el.hasAttribute('aria-label')) {
            el.setAttribute('aria-label', ariaLabel);
          }
        }, { root: sr });
        subs.add(sub);
      } catch (e) {
        // Host never produced an open shadowRoot within timeout – ignore.
      }
    });

    return {
      disconnect() {
        outer.disconnect();
        for (const s of subs) s.disconnect();
        subs.clear();
      }
    };
  }

  // Export
  const A11yWait = {
    waitForElement,
    waitForOpenShadowRootOf,
    observeMatches,
    labelWhenPresent,
    labelWhenPresentDeep,
    observeMatchesDeep,
    observeHostsAndLabelInside,
  };

  // Attach to window and CommonJS for convenience
  if (typeof window !== 'undefined') window.A11yWait = A11yWait;
  if (typeof module !== 'undefined' && module.exports) module.exports = A11yWait;

  /*
    --- Usage Examples ---

    // 1) Label first match in light DOM
    // A11yWait.labelWhenPresent('.product-form__input', 'Product form');

    // 2) Label across all (open) shadow roots
    // A11yWait.labelWhenPresentDeep('.buy-button', 'Buy now', { timeout: 10000 });

    // 3) Label all current + future matches in light DOM
    // const sub = A11yWait.observeMatches('.price', el => el.setAttribute('aria-label', 'Price'));
    // sub.disconnect();

    // 4) Many hosts: for each <my-widget>, label elements inside its shadowRoot
    // const h = A11yWait.observeHostsAndLabelInside('my-widget', '.target', 'Accessible label');
    // h.disconnect();

    // 5) Deep observe (all roots) and run custom handler once per element
    // const deepSub = A11yWait.observeMatchesDeep('[data-track]', el => console.log('Found', el));
    // deepSub.disconnect();
  */
})();
